import * as React from 'react'
import isEqual from 'lodash.isequal'
import {
  Block,
  getBlockUpdateOverride,
  getValueBasedOnSelectedVariants,
  type CodeSnippetBlock as TCodeSnippetBlock,
} from '@basehub/data/replicache/index'

import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { CodeEditor } from '~/sotano-kit/primitives/code-editor'
import { useBlockInputBaseProps } from '../../block-wrapper'
import { Portal } from '~/components/portal'
import { useHasRendered } from '~/hooks/use-has-rendered'
import { LANGUAGES, Language } from '@basehub/shared-constants'
import { useSyncedValue } from '~/hooks/use-synced-value'
import { useToggleState } from '~/hooks/use-toggle-state'
import { CodeSnippetControls } from '../controls'
import { prettierAllowedLanguages } from '@basehub/utils/format'
import { useSelectedVariants } from '~/hooks/use-selected-variants'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import { UpdateBlockParams } from '@basehub/data/replicache/mutators/repo-space/block/mutators/crud'
import { useRepoUndoManager } from '~/replicache/frontend/undo-redo/manager'
import { useTabsStore } from '../../../tabs/store'

import s from './input.module.scss'

export const CodeSnippetBlockInput = ({
  block,
  parent,
  path,
  showControls = true,
  ...props
}: {
  block: TCodeSnippetBlock
  parent: Block
  path: string
  readOnly?: boolean
  showControls?: boolean
}) => {
  const { data } = useRepoReplicache()
  const replicache = data?.replicache
  const baseProps = useBlockInputBaseProps(block)
  const hasRendered = useHasRendered()
  const selectedDashboardVariants = useSelectedVariants({
    parentBlock: parent,
    block,
  })
  const undoManager = useRepoUndoManager()
  const activeTabKey = useTabsStore((s) => s.activeTab?.key)

  const handleLanguageChange = React.useCallback(
    (language: Language) =>
      replicache?.mutate.updateBlock({
        id: block.id,
        instanceChildTarget: block.instanceChildTarget,
        data: getBlockUpdateOverride({
          newValue: { language },
          block,
          selectedVariants: selectedDashboardVariants,
        }),
        path,
        strategy: 'merge-deep',
        isoNow: new Date().toISOString(),
      }),
    [block, path, replicache?.mutate, selectedDashboardVariants]
  )

  const value = React.useMemo(
    () =>
      getValueBasedOnSelectedVariants({
        block,
        variantsToFilterBy: selectedDashboardVariants,
      }),
    [block, selectedDashboardVariants]
  )

  const [lastSelectedVariants, setLastSelectedVariants] = React.useState(
    () => selectedDashboardVariants
  )

  const { value: code, onChange: onCodeChange } = useSyncedValue<
    unknown,
    string
  >({
    value: value.code,
    customEventsKey: `synced-value:code-snippet:${idToSourceBlockId(block)}`,
    setValue: React.useCallback(
      async (value, { internalValueRef, customEventsKey }) => {
        if (!replicache?.mutate) return
        const getParams = (v: string): UpdateBlockParams => ({
          id: block.id,
          instanceChildTarget: block.instanceChildTarget,
          data: getBlockUpdateOverride({
            newValue: { code: v },
            block,
            selectedVariants: selectedDashboardVariants,
          }),
          path,
          strategy: 'merge-deep',
          isoNow: new Date().toISOString(),
        })
        const { undo } = await replicache.mutate.updateBlock(getParams(value))
        if (undo) {
          undoManager.current?.push({
            redo: [
              {
                op: 'focus',
                args: {
                  preferredTabKey: activeTabKey,
                  focus: {
                    type: 'block:value',
                    blockId: idToSourceBlockId({
                      id: block.id,
                      sourceBlockId: block.sourceBlockId,
                    }),
                  },
                },
              },
              { op: 'updateBlock', args: getParams(internalValueRef.current) },
              {
                op: 'customWindowEvent',
                args: { name: customEventsKey, detail: { action: 'sync' } },
              },
            ],
            undo: [
              {
                op: 'focus',
                args: {
                  preferredTabKey: activeTabKey,
                  focus: {
                    type: 'block:value',
                    blockId: idToSourceBlockId({
                      id: block.id,
                      sourceBlockId: block.sourceBlockId,
                    }),
                  },
                },
              },
              { op: 'blockBatchMutations', args: { mutations: undo } },
              {
                op: 'customWindowEvent',
                args: { name: customEventsKey, detail: { action: 'sync' } },
              },
            ],
          })
        }
      },
      [
        block,
        activeTabKey,
        path,
        replicache?.mutate,
        selectedDashboardVariants,
        undoManager,
      ]
    ),
    shouldSync: () => {
      if (!isEqual(lastSelectedVariants, selectedDashboardVariants)) {
        setLastSelectedVariants(selectedDashboardVariants)
        return true
      }
      return false
    }, // sync when selected variants change
    rateLimitMs: 1000,
  })

  const selectLanguageToggleState = useToggleState()

  return (
    <>
      {hasRendered && showControls && (
        <Portal
          id={`more-actions-portal-${block.id}`}
          className="flex items-center gap-0.5 mr-2"
        >
          <CodeSnippetControls
            copyContent={code}
            formatting={
              prettierAllowedLanguages.includes(value.language)
                ? {
                    code: code,
                    lang: value.language,
                    onFormat: onCodeChange,
                  }
                : undefined
            }
            languageSelector={{
              value: value.language,
              onValueChange: (v) => {
                handleLanguageChange(v as Language)
              },
              isOpen: selectLanguageToggleState.isOn,
              onOpenChange: selectLanguageToggleState.handleToggle,
              options: Object.entries(LANGUAGES)
                .filter((lang) => {
                  if (block.constraints.allowedLanguages === null) return true
                  if (value.language === lang[0]) return true
                  return block.constraints.allowedLanguages.includes(lang[0])
                })
                .sort((a, b) => a[1].label.localeCompare(b[1].label))
                .map(([key, value]) => ({
                  value: key,
                  label: value.label,
                  disabled: block.constraints.allowedLanguages
                    ? !block.constraints.allowedLanguages.includes(key)
                    : false,
                })),
            }}
          />
        </Portal>
      )}

      <div
        style={{ minHeight: 150 }}
        className={s['container']}
        onPointerDown={(e) => {
          if (
            e.target instanceof HTMLElement &&
            e.target.classList.contains('cm-editor')
          ) {
            const content = e.target.querySelector('[contenteditable]')
            if (content && content instanceof HTMLElement) {
              e.preventDefault()
              content.focus()
            }
          }
        }}
      >
        <CodeEditor
          key={selectedDashboardVariants.map((v) => v.variantId).join(',')}
          {...baseProps}
          {...props}
          value={code}
          onChange={(v) => onCodeChange(v)}
          language={
            (Object.keys(LANGUAGES).find((key) => value.language === key) ??
              null) as Language | null
          }
        />
      </div>
    </>
  )
}
